/**
 * <AuthImage> — renders a protected image.
 *
 * Plain <img src> can't send the Authorization header the admin file
 * endpoints require, so this fetches the bytes as a Blob through the
 * axios client (auth attached by interceptor), then renders an object
 * URL. Blob is cached by TanStack Query; the object URL is created
 * per-mount and revoked on unmount.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

async function fetch_blob(path: string): Promise<Blob> {
  // `no_session_teardown`: an image that 401s means this file isn't readable,
  // not that the session died. Without it a single mis-scoped thumbnail logs
  // the admin out of the whole console.
  const res = await api.get<Blob>(path, { responseType: 'blob', no_session_teardown: true });
  return res.data;
}

export function use_file_blob(path: string | null) {
  return useQuery({
    queryKey: ['file-blob', path],
    queryFn: () => fetch_blob(path as string),
    enabled: path !== null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Fetch a protected file and open it in a new tab (full-size view / PDFs).
 *
 * Callers must handle the rejection. This used to be attached straight to an
 * unawaited `onClick`, so a 404 on a broken prescription produced no tab, no
 * message and no clue — only an uncaught rejection in the console.
 */
export async function open_file_in_new_tab(path: string): Promise<void> {
  const blob = await fetch_blob(path);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * `onClick`-safe wrapper: reports failure through `on_error` instead of
 * vanishing into an unhandled rejection.
 */
export function open_file_handler(path: string, on_error: (msg: string) => void): () => void {
  return () => {
    void open_file_in_new_tab(path).catch((err) =>
      on_error(err instanceof Error ? err.message : 'Could not open this file'),
    );
  };
}

export function AuthImage({
  path,
  alt,
  className,
  'data-testid': testid,
}: {
  path: string;
  alt: string;
  className?: string;
  'data-testid'?: string;
}) {
  const blob_q = use_file_blob(path);
  const [object_url, set_object_url] = useState<string | null>(null);

  useEffect(() => {
    if (!blob_q.data) return;
    const url = URL.createObjectURL(blob_q.data);
    set_object_url(url);
    return () => URL.revokeObjectURL(url);
  }, [blob_q.data]);

  if (blob_q.isError) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ''}`}
        data-testid={testid}
        title="Preview unavailable"
      >
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (!object_url) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ''}`}
        data-testid={testid}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  return <img src={object_url} alt={alt} className={className} data-testid={testid} loading="lazy" />;
}
